var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { Box, Button, FormHelperText, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ArrowLeft02Icon, CheckmarkCircle01Icon } from 'hugeicons-react';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLazyGetMessageFeedbackQuery, useResolveMessageFeedbackMutation } from '../../../../../../../application/features/chat/chat-api';
import { ResolutionType } from '../../../../../../../application/features/chat/enums/resolution-type';
import useTranslate from '../../../../../../../localization/helpers';
import ProtectedElement from '../../../../../../components/ProtectedElement';
import RichText from '../../../../../../components/RichText';
import TextEditor from '../../../../../../components/TextEditor';
import { Permissions } from '../../../../../../permissions';
import AiMessageTraining from '../chat-info/components/ai-message-training';
var TrainingSegmentDetails = function (_a) {
    var _b;
    var activeMessage = _a.activeMessage, setActiveMessage = _a.setActiveMessage, messages = _a.messages, handleBackToChatInfo = _a.handleBackToChatInfo;
    var theme = useTheme();
    var _c = useTranslate(), translate = _c.translate, tWithField = _c.tWithField;
    var isXs = useMediaQuery(theme.breakpoints.only('xs'));
    var isscreen600900 = useMediaQuery('(min-width:600px) and (max-width:900px)');
    var _d = __read(useState(false), 2), updateFeedback = _d[0], setUpdateFeedback = _d[1];
    var _e = __read(useResolveMessageFeedbackMutation(), 2), resolveFeedback = _e[0], _f = _e[1], resolveLoading = _f.isLoading, isSuccess = _f.isSuccess;
    var _g = __read(useLazyGetMessageFeedbackQuery(), 2), getFeedback = _g[0], _h = _g[1], feedback = _h.data, isFetching = _h.isFetching;
    var currentActiveIndex = messages.findIndex(function (m) { return m.message_id === (activeMessage === null || activeMessage === void 0 ? void 0 : activeMessage.message_id); });
    var totalMessages = messages.length;
    var showResolution = !isFetching && (feedback === null || feedback === void 0 ? void 0 : feedback.resolutionType) === ResolutionType.Resolved && !updateFeedback;
    var _j = useForm({
        defaultValues: {
            userId: '',
            conversationId: '',
            messageId: '',
            correctedAIResponse: ''
        }
    }), control = _j.control, reset = _j.reset, setValue = _j.setValue, clearErrors = _j.clearErrors, errors = _j.formState.errors, handleSubmit = _j.handleSubmit;
    useEffect(function () {
        if (activeMessage)
            getFeedback({
                conversationId: activeMessage.conversation_id,
                messageId: activeMessage.message_id,
                userId: activeMessage.user_id
            });
    }, [activeMessage, getFeedback]);
    useEffect(function () {
        setUpdateFeedback(false);
        if (feedback) {
            reset({
                conversationId: feedback.conversationId,
                messageId: feedback.messageId,
                userId: feedback.userId,
                correctedAIResponse: feedback.suggestedResponse
            });
        }
    }, [feedback, reset]);
    useEffect(function () {
        if (isSuccess) {
            setUpdateFeedback(false);
            enqueueSnackbar(tWithField('toolTips.resolveSuccess', 'common.feedback'), {
                autoHideDuration: 3000,
                variant: 'success',
                anchorOrigin: { horizontal: 'right', vertical: 'top' }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);
    var resetFeedback = function () {
        if (feedback) {
            setUpdateFeedback(true);
            reset({
                conversationId: feedback.conversationId,
                messageId: feedback.messageId,
                userId: feedback.userId,
                correctedAIResponse: feedback.correctedAIResponse
            });
        }
    };
    var showNextFeedback = function () {
        if (activeMessage) {
            var currentIndex = messages.findIndex(function (m) { return m.message_id === activeMessage.message_id; });
            setActiveMessage(messages[currentIndex + 1]);
        }
    };
    var showPreviousFeedback = function () {
        if (activeMessage) {
            var currentIndex = messages.findIndex(function (m) { return m.message_id === activeMessage.message_id; });
            setActiveMessage(messages[currentIndex - 1]);
        }
    };
    var onSubmit = function (resolution) {
        resolution.resolutionRequired = true;
        resolveFeedback(resolution);
    };
    var resolveWithoutCorrection = function () {
        setValue('correctedAIResponse', '');
        clearErrors('correctedAIResponse');
        setTimeout(function () {
            var resolution = {
                conversationId: feedback.conversationId,
                messageId: feedback.messageId,
                userId: feedback.userId,
                correctedAIResponse: '',
                resolutionRequired: false
            };
            resolveFeedback(resolution);
        }, 500);
    };
    return (_jsxs(Box, { sx: {
            width: '100%',
            height: '100%',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 10px 48px 0 #919EAB29',
            borderRadius: '24px',
            borderTopLeftRadius: isXs ? '24px' : '0px',
            borderBottomLeftRadius: isXs ? '24px' : '0px'
        }, children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    width: '100%',
                    flexDirection: isXs ? 'column' : 'row',
                    justifyContent: 'space-between',
                    borderRadius: '16px',
                    padding: '16px 20px'
                }, children: [isXs || isscreen600900 ? (_jsxs(Box, { display: 'flex', flexDirection: 'row', gap: 0.2, alignItems: 'center', children: [_jsx(IconButton, { color: 'primary', onClick: handleBackToChatInfo, children: _jsx(ArrowLeft02Icon, { size: 24 }) }), _jsx(Typography, { variant: 'h6', sx: { color: '#161C24' }, children: translate('training.segmentDetails') })] })) : (_jsx(Typography, { variant: 'h6', sx: { color: '#161C24' }, children: translate('training.segmentDetails') })), _jsxs(Box, { display: 'flex', mt: isXs ? 1 : 0, flexDirection: 'row', justifyContent: feedback && feedback.resolutionType !== ResolutionType.None && !updateFeedback
                            ? 'space-between !important'
                            : 'flex-end', gap: 0.5, alignItems: 'center', children: [feedback && feedback.resolutionType !== ResolutionType.None && !updateFeedback && (_jsx(ProtectedElement, { permissions: [Permissions.Training.Update], children: _jsx(Button, { size: 'medium', variant: 'contained', color: 'primary', startIcon: _jsx(CheckmarkCircle01Icon, {}), onClick: resetFeedback, children: translate('training.resolved') }) })), currentActiveIndex >= 0 && (_jsxs(Box, { alignItems: 'center', display: 'flex', children: [_jsxs(Typography, { variant: 'caption', color: theme.palette.text.tertiary, children: [currentActiveIndex + 1, " 0f ", totalMessages] }), _jsx(IconButton, { size: 'small', onClick: showPreviousFeedback, disabled: currentActiveIndex === 0, children: _jsx(Icon, { icon: 'eva:arrow-upward-fill', fontSize: 24 }) }), _jsx(IconButton, { size: 'small', onClick: showNextFeedback, disabled: currentActiveIndex === totalMessages - 1, children: _jsx(Icon, { icon: 'eva:arrow-downward-fill', fontSize: 24 }) })] }))] })] }), feedback && (_jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    maxHeight: isXs ? 'calc(100svh - 407px)' : 'calc(100svh - 323px)',
                    paddingBottom: '100px !important',
                    padding: '0px 20px',
                    width: '100%',
                    borderRadius: '16px',
                    gap: '12px'
                }, children: [(isXs || isscreen600900) && _jsx(AiMessageTraining, { message: activeMessage }), _jsxs(Box, { sx: {
                            backgroundColor: '#F4F6F8',
                            borderRadius: '16px',
                            padding: '12px 16px',
                            gap: '12px',
                            boxShadow: '0 4px 8px 0 #919EAB29'
                        }, children: [_jsx(Typography, { variant: 'h6', color: theme.palette.grey[700], children: translate('training.userResponse') }), _jsxs(Box, { display: 'flex', flexDirection: 'column', marginTop: 1, gap: '20px', children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', children: [_jsxs(Typography, { variant: 'body2', color: theme.palette.grey[700], children: ["\uD83D\uDC4D ", translate('training.suggested')] }), _jsx(RichText, { content: feedback.suggestedResponse })] }), _jsxs(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', children: [_jsxs(Typography, { variant: 'body2', color: theme.palette.grey[700], children: ["\u2139\uFE0F ", translate('chat.moreDetails')] }), _jsx(Typography, { variant: 'body2', children: feedback.moreDetails })] })] })] }), ((feedback && feedback.resolutionType === ResolutionType.None) || updateFeedback) && (_jsxs(Box, { mt: 2, children: [_jsx(Controller, { control: control, name: 'correctedAIResponse', rules: {
                                    required: {
                                        value: true,
                                        message: tWithField('validations.fieldIsRequired', 'training.correctedAiResponse')
                                    },
                                    validate: function (value) {
                                        return value.trim() !== '' ||
                                            tWithField('validations.fieldIsRequired', 'training.correctedAiResponse');
                                    }
                                }, render: function (_a) {
                                    var _b = _a.field, onChange = _b.onChange, value = _b.value;
                                    return (_jsx(ProtectedElement, { permissions: [Permissions.Training.Update], action: 'hide', children: _jsx(TextEditor, { value: value, onChange: onChange, placeholder: translate('training.correctedAiResponse') }) }));
                                } }), _jsx(FormHelperText, { error: true, children: (_b = errors.correctedAIResponse) === null || _b === void 0 ? void 0 : _b.message }), _jsx(Box, { marginTop: 2, children: _jsxs(Box, { display: 'flex', justifyContent: 'flex-end', flexDirection: isXs ? 'column' : 'row', gap: 1, children: [_jsx(Box, { children: _jsx(ProtectedElement, { permissions: [Permissions.Training.Update], action: 'hide', children: _jsx(Button, { disabled: resolveLoading, onClick: resolveWithoutCorrection, size: 'medium', variant: 'contained', color: 'error', startIcon: _jsx(Icon, { icon: 'icons8:cancel-2' }), children: translate('training.doesNotRequireResolution') }) }) }), _jsx(Box, { children: _jsx(ProtectedElement, { permissions: [Permissions.Training.Update], action: 'hide', children: _jsx(Button, { disabled: resolveLoading, onClick: handleSubmit(onSubmit), size: 'medium', variant: 'contained', color: 'inherit', startIcon: _jsx(CheckmarkCircle01Icon, {}), children: translate('training.resolveToTrain') }) }) })] }) })] })), showResolution && (_jsxs(Box, { sx: {
                            backgroundColor: '#F4F6F8',
                            borderRadius: '16px',
                            padding: '12px 16px',
                            gap: '12px',
                            boxShadow: '0 4px 8px 0 #919EAB29'
                        }, children: [_jsxs(Typography, { variant: 'h6', color: theme.palette.grey[700], children: [translate('training.correctedAiResponse'), _jsxs("span", { style: { fontSize: '16px', fontWeight: 400, color: theme.palette.grey[700] }, children: ["(", translate('training.forTraining'), ")"] })] }), _jsx(Box, { display: 'flex', flexDirection: 'column', marginTop: 1, gap: '20px', children: _jsx(Box, { display: 'flex', flexDirection: 'column', justifyContent: 'center', children: _jsx(RichText, { content: feedback.correctedAIResponse }) }) })] }))] }))] }));
};
export default TrainingSegmentDetails;
